import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import * as boardsStore from '../extensions/bundled/task-manager/boardsStore';
import { withDoneAt } from '../extensions/bundled/task-manager/taskStatus';
import { bumpBoards } from '../extensions/bundled/task-manager/boardsUiStore';
import TaskDetailModal from './TaskDetailModal';

/**
 * One task, opened straight from a chat share card — the card's own modal,
 * without leaving the conversation for the board it lives on.
 *
 * The hard part is not rendering it, it is WRITING. TaskBoardTab owns the
 * board's real write pipeline (an in-memory board, a dirty queue, retry on
 * failure, a team-sync flush), and none of that exists over here in chat. So
 * every save does a READ-MODIFY-WRITE against the stored board rather than
 * persisting a board this modal has been holding:
 *
 *   read the board as it is on disk NOW -> replace the one task -> write
 *
 * That matters because the board tab may be open on the same board in another
 * tab, saving its own edits. Writing a snapshot captured when this modal
 * opened would silently roll back everything that landed in between. Re-reading
 * narrows the race to the single task being edited, which is the same shape
 * (and the same LWW resolution) as two devices editing one task.
 *
 * Saves are debounced because description keystrokes arrive continuously, and
 * flushed on close so nothing is lost by dismissing quickly.
 */

// Long enough to coalesce typing, short enough that closing rarely has to
// flush anything.
const SAVE_DEBOUNCE_MS = 700;

export default function TaskQuickViewModal({ boardId, taskId, teamId, onClose }) {
  const [board, setBoard] = useState(null);
  const [failed, setFailed] = useState(false);
  const pendingPatchRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const scope = teamId != null ? boardsStore.teamScopeId(teamId) : undefined;
        const loaded = await boardsStore.readBoard(boardId, scope);
        if (cancelled) return;
        if (!loaded) setFailed(true);
        else setBoard(loaded);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [boardId, teamId]);

  const flush = useCallback(async () => {
    const patch = pendingPatchRef.current;
    pendingPatchRef.current = null;
    clearTimeout(timerRef.current);
    if (!patch) return;
    try {
      // Re-read, do not reuse `board` — see the module note.
      const scope = teamId != null ? boardsStore.teamScopeId(teamId) : undefined;
      const fresh = await boardsStore.readBoard(boardId, scope);
      if (!fresh) return;
      const next = {
        ...fresh,
        tasks: fresh.tasks.map((t) =>
          String(t.id) === String(taskId)
            ? { ...t, ...withDoneAt(t, patch), updatedAt: new Date().toISOString() }
            : t
        ),
        updatedAt: new Date().toISOString(),
      };
      await boardsStore.writeBoard(next);
      // Tell an open board tab to reload, or it would keep rendering the task
      // as it was before this modal touched it.
      bumpBoards();
    } catch {
      // Best effort: a failed save must not trap the user in the modal. The
      // board tab remains the place with retry.
    }
  }, [boardId, taskId, teamId]);

  const onChange = useCallback(
    (patch) => {
      // Local echo first, so typing is not gated on a disk round trip.
      setBoard((prev) =>
        prev
          ? {
              ...prev,
              tasks: prev.tasks.map((t) =>
                String(t.id) === String(taskId) ? { ...t, ...withDoneAt(t, patch) } : t
              ),
            }
          : prev
      );
      pendingPatchRef.current = { ...(pendingPatchRef.current || {}), ...patch };
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(flush, SAVE_DEBOUNCE_MS);
    },
    [taskId, flush]
  );

  // Flush on unmount as well as on an explicit close: closing the whole chat
  // view unmounts this without ever calling onClose.
  useEffect(() => () => { flush(); }, [flush]);

  const close = useCallback(() => {
    flush();
    onClose?.();
  }, [flush, onClose]);

  if (failed) return null;
  const task = board?.tasks?.find((t) => String(t.id) === String(taskId));
  if (!task) return null;

  // Portalled: the share card lives inside a chat message row, whose z-index
  // makes a stacking context — the modal's own z-50 then only ranked it within
  // that row, and the chat pane's divider and composer icons painted over it.
  return createPortal(
    <TaskDetailModal
      task={task}
      teamId={board.visibility === 'team' && board.teamId != null ? board.teamId : null}
      boardId={board.id}
      boardLabels={board.labels}
      isActive
      onClose={close}
      onChange={onChange}
      // Deleting a task is not offered from here. The modal is a quick look
      // from a conversation, and a destructive action reached by following a
      // link someone else sent is the wrong place for it — the board is one
      // click away and has the confirmation flow.
      onDelete={null}
    />,
    document.body,
  );
}
